<script setup>
import EnTextField from 'Components/inputs/EnTextFieldV2/EnTextFieldV2.vue';
import EnOverlaySpinner from 'Components/feedback/EnOverlaySpinner/EnOverlaySpinner';
import EnWidgetCard from 'Components/widgets/EnWidgetCard/EnWidgetCard.vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import { EnButtonVariants } from 'Components/types/EnButtonVariants';

import { getUrlParams } from 'Js/utils/urlUtils';
import { useSessionStorage } from 'Components/navigation/utils/useSessionStorage';
import { required, validEmail } from 'Js/validation/rules';

const {getIsEnTab} = useSessionStorage();
const isEnTab = computed(() => {
  return getIsEnTab();
});
const returnUrl = ref('');
const email = ref('');
const loading = ref(false);
const emailRules = ref([
  required,
  validEmail
]);
const isEmailValid = computed(() => {
  return required(email.value) === true && validEmail(email.value) === true;
});
const successRequest = ref(null);
const titleMessage = computed(() => {
  if (successRequest.value === null) {
    return t('Authentication.password.forgot.title');
  }
  return t('Authentication.password.forgot.successMessage');
});
const backToLoginUrl = computed(() => {
  return `/Account/Login${isEnTab.value ? 'EnTab' : ''}${returnUrl.value ? '?returnUrl=' + encodeURIComponent(returnUrl.value) : ''}`;
});

async function sendRequest() {
  loading.value = true;
  const response = await fetch(`/api/Manage/ForgotPassword?email=${email.value}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  });

  successRequest.value = response.ok;
  loading.value = false;
}

onBeforeMount(async () => {
  const params = getUrlParams(window.location);

  returnUrl.value = params['returnurl'];
});
</script>

<template>
  <div class="main-overview restricted-width">
    <en-overlay-spinner v-model="loading"></en-overlay-spinner>
    <en-widget-card
        icon-class="icon-enloc_logo_min"
        :title="titleMessage"
        :max-width="450"
        :padding="32"
    >
      <div class="form-group-bordered">
        <div>
          <div class="form-group" v-if="successRequest === null">
            <div class="label">{{ t('Authentication.password.forgot.label') }}</div>
            <en-text-field
                variant="outlined"
                rounded="lg"
                type="email"
                density="compact"
                v-model="email"
                :rules="emailRules"
            ></en-text-field>
          </div>

          <div class="login-block mt-6">
              <en-button
                  static-id="forgot-password-button"
                  v-if="successRequest === null"
                  :disabled="!isEmailValid"
                  no-shadow
                  :variant="EnButtonVariants.FILLED"
                  class="button-text"
                  width="100%"
                  @click="sendRequest"
              >
                {{ t('Authentication.password.forgot.buttonText') }}
              </en-button>

              <div v-if="successRequest === null" class="d-flex align-center justify-center w-100">
                <v-divider class="flex-grow-1"></v-divider>
                <span class="or-text px-3">{{ t('common.or') }}</span>
                <v-divider class="flex-grow-1"></v-divider>
              </div>

              <a :href="backToLoginUrl" class="w-100">
                <en-button
                    static-id="back-to-login-button"
                    no-shadow
                    class="button-text"
                    width="100%"
                >
                  {{ t('Authentication.password.backToLogin') }}
                </en-button>
              </a>
          </div>
        </div>
      </div>
    </en-widget-card>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.main-overview {
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.or-text {
  color: variables.$login-page-find-or-text;
}

.form-group-bordered {
  margin-top: 15px;
}

.button-text {
  border: 1px solid variables.$login-page-btn-text;
  text-transform: none;
}

.label {
  display: block;
  font-size: 1rem;
  color: variables.$en-blue !important;
  font-weight: 300;
}

.login-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.form-group {
  text-align: left;
}

.form-group,
.form-group-bordered {
  width: 100%;
}

</style>